import { useEffect, useState, useRef } from "react";
import { Button, Typography, Card, Box, TextField, Grid } from "@mui/material";
import PageHeader from "src/@core/components/page-header";
import Translations from "src/layouts/components/Translations";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { DefaultPaginationSettings } from "src/constants/general.const";
import { toastError, toastSuccess } from "src/utils/utils";
import DialogConfirmation from "src/views/dialog/DialogConfirmation";
import PermissionListTable from "src/views/tables/PermissionListTable";
import { useNavigate } from "react-router-dom";

const PermissionsPage = () => {
  const searchTimeoutRef = useRef();
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [search, setSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    DefaultPaginationSettings.ROWS_PER_PAGE
  );
  const navigate = useNavigate();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState(null);

  const toggleConfirm = (e, permission = null) => {
    setConfirmOpen((prev) => !prev);
    setPermissionToDelete(permission);
  };

  const fetchData = ({ currentPage, pageSize, search }) => {
    setLoading(true);
    const params = { page: currentPage, limit: pageSize, search };

    axiosInstance
      .get(ApiEndPoints.PERMISSIONS.list, { params })
      .then((response) => {
        setPermissions(response.data?.data?.permission || []);
        setTotalCount(response.data?.data?.totalRecords || 0);
      })
      .catch((error) => toastError(error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData({ currentPage, pageSize, search });
  }, [currentPage, pageSize, search]);

  const handleSearchChange = (e) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setSearch(e.target.value);
      setCurrentPage(1);
    }, 500);
  };

  const onConfirmDelete = () => {
    axiosInstance
      .delete(ApiEndPoints.PERMISSIONS.delete(permissionToDelete?.id))
      .then((response) => {
        toastSuccess(response.data?.message || "Permission deleted successfully");
        fetchData({ currentPage, pageSize, search });
        setConfirmOpen(false);
        setPermissionToDelete(null);
      })
      .catch((error) => {
        toastError(error);
      });
  };

  return (
    <>
      <Grid container spacing={4} padding={2}>
        <Grid item xs={12}>
          <PageHeader
            title={
              <Typography variant="h5">
                <Translations text="Permissions" />
              </Typography>
            }
            action={
              <Button 
                variant="contained" 
                color="primary"   
                onClick={() => navigate("/permissions/add")}
              >
                Add Permission
              </Button>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Card>
            <Box
              sx={{
                p: 5,
                pb: 0,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "end",
              }}
            >
              <TextField
                type="search"
                size="small"
                placeholder="Search"
                onChange={handleSearchChange}
              />
            </Box>
            <Box sx={{ p: 5 }}>
              <PermissionListTable
                rows={permissions}
                loading={loading}
                totalCount={totalCount}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                setPageSize={setPageSize}
                pageSize={pageSize}
                toggleDelete={toggleConfirm}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      <DialogConfirmation
        open={confirmOpen}
        toggle={toggleConfirm}
        title="Delete Permission"
        subtitle="Are you sure you want to delete this permission?"
        onConfirm={onConfirmDelete}
      />
    </>
  );
};

export default PermissionsPage;
